import React, { useCallback, useEffect, useMemo } from "react";

import { IQueryOptions } from "api/api-hooks/api-hooks-common";
import { useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import {
  ChartDataset,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line } from "react-chartjs-2";
import { AdvancedProps, Datapoint, LineConfig, ValueLabel } from "../sparkline/SparkLineTypes";

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Filler);

type SparkLineChartProps = {
  dataArray: number[];
  advancedProps: AdvancedProps;
  lineColor?: string;
  lineWidth?: number;
  chartWidth?: number;
  chartHeight?: number;
  currentBiggestValueWidth?: number;
  setValueLabel: React.Dispatch<React.SetStateAction<ValueLabel>>;
  minYAxis?: number;
  maxYAxis?: number;
};

const options = (showYAxis = false, minYAxis?: number, maxYAxis?: number): ChartOptions<"line"> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 0,
  },
  plugins: {
    tooltip: {
      enabled: false,
    },
    legend: {
      display: false,
    },
  },
  interaction: {
    intersect: false,
  },
  scales: {
    x: {
      display: false,
      type: "time",
    },
    y: {
      display: showYAxis && "auto",
      min: minYAxis,
      max: maxYAxis,
    },
  },
});

const SparkLineChart: React.FC<SparkLineChartProps> = ({
  dataArray,
  advancedProps,
  lineColor,
  lineWidth,
  chartWidth,
  chartHeight,
  setValueLabel,
  currentBiggestValueWidth,
  minYAxis,
  maxYAxis,
}) => {
  const startDate = useMemo(() => advancedProps.data?.[0].x ?? new Date(), [advancedProps.data]);
  const endDate = useMemo(
    () => advancedProps.data?.[advancedProps.data.length - 1].x ?? new Date(),
    [advancedProps.data]
  );

  const queryParams = useCallback(
    (identifier?: string): [string[], { timeStart: string; timeEnd: string }, IQueryOptions] => [
      [identifier!],
      {
        timeStart: startDate?.toISOString(),
        timeEnd: endDate?.toISOString(),
      },
      {
        refetchInterval: 0,
        enabled: !!identifier,
        keepPreviousData: true,
      },
    ],
    [endDate, startDate]
  );

  const { data: baselineDatasetData } = useGetDataInfoRange(
    ...queryParams(advancedProps.baseline?.dataset)
  );
  const { data: minLineDatasetData } = useGetDataInfoRange(
    ...queryParams(advancedProps.minLine?.dataset)
  );
  const { data: maxLineDatasetData } = useGetDataInfoRange(
    ...queryParams(advancedProps.maxLine?.dataset)
  );

  const getValues = useCallback(
    (config?: LineConfig, data?: InstantDoubleValue[]) => {
      if (config?.usage === "constant") {
        return [...advancedProps.data].map(item => ({ ...item, y: config.constant ?? 0 }));
      }
      if (config?.usage === "dataset" && data) {
        return data.map((item, index) => {
          let date = new Date(item.key);
          if (index === 0) date = new Date(startDate);
          if (index === data.length - 1) date = new Date(endDate);
          return {
            y: item.value,
            x: date,
          };
        });
      }
      return [];
    },
    [advancedProps.data, endDate, startDate]
  );

  const baselineValues = useMemo(
    () => getValues(advancedProps.baseline, baselineDatasetData?.[0]?.data),
    [advancedProps.baseline, baselineDatasetData, getValues]
  );
  const minValues = useMemo(
    () => getValues(advancedProps.minLine, minLineDatasetData?.[0]?.data),
    [advancedProps.minLine, getValues, minLineDatasetData]
  );
  const maxValues = useMemo(
    () => getValues(advancedProps.maxLine, maxLineDatasetData?.[0]?.data),
    [advancedProps.maxLine, getValues, maxLineDatasetData]
  );

  useEffect(() => {
    if (dataArray.at(-1)! >= maxValues.at(-1)?.y!) {
      setValueLabel(prev => ({ ...prev, textColor: advancedProps.maxLine?.color }));
    } else if (dataArray.at(-1)! <= minValues.at(-1)?.y!) {
      setValueLabel(prev => ({ ...prev, textColor: advancedProps.minLine?.color }));
    } else {
      setValueLabel(prev => ({ ...prev, textColor: "inherit" }));
    }
  }, [
    advancedProps.maxLine?.color,
    advancedProps.minLine?.color,
    dataArray,
    maxValues,
    minValues,
    setValueLabel,
  ]);

  const baselineDataChart: ChartDataset<"line", Datapoint[]> = {
    data: baselineValues,
    borderColor: advancedProps.baseline?.color,
    borderWidth: advancedProps.baseline?.width,
    pointStyle: false,
  };
  const minValueLine: ChartDataset<"line", Datapoint[]> = {
    data: minValues,
    borderColor: advancedProps.minLine?.color,
    borderWidth: 1,
    borderDash: [2, 5],
    pointStyle: false,
  };
  const maxValueLine: ChartDataset<"line", Datapoint[]> = {
    data: maxValues,
    borderColor: advancedProps.maxLine?.color,
    borderWidth: 1,
    borderDash: [3, 5],
    pointStyle: false,
  };

  const lineOptions = options(advancedProps.showYAxis, minYAxis, maxYAxis);

  let currentChartWidth =  chartWidth ? `${chartWidth}px` : "100%"

  if (currentBiggestValueWidth) {
    currentChartWidth = `calc(100% - ${currentBiggestValueWidth}px)`
  }

  return (
    <div
      style={{
        width: currentChartWidth,
        height: chartHeight ? `${chartHeight}px` : "100%",
      }}
    >
      <Line
        data={{
          datasets: [
            {
              data: advancedProps.data,
              borderColor: "transparent",
              borderWidth: 0,
              pointStyle: false,

              fill: {
                target: 4,
                below: advancedProps.minLine?.color,
                above: "transparent",
              },
            },
            {
              data: advancedProps.data,
              borderColor: "transparent",
              borderWidth: 0,
              pointStyle: false,

              fill: {
                target: 5,
                below: "transparent",
                above: advancedProps.maxLine?.color,
              },
            },
            {
              data: advancedProps.data,
              borderColor: lineColor,
              borderWidth: lineWidth,
              pointStyle: false,

              fill: {
                target: 3,
                above: advancedProps.baseline?.fillAboveColor,
                below: advancedProps.baseline?.fillBelowColor,
              },
            },
            baselineDataChart,
            minValueLine,
            maxValueLine,
          ],
        }}
        options={lineOptions}
        style={{ flex: 2 }}
      />
    </div>
  );
};

export default SparkLineChart;
